/*
 * AREngine.js — Go AR · Core
 */

import { MindARThree } from 'mind-ar/dist/mindar-image-three.prod.js';
import * as THREE from 'three';

export class AREngine {

  constructor(container) {
    this.container = container;
    this.mindar    = null;
    this.renderer  = null;
    this.scene     = null;
    this.camera    = null;
    this._clock    = new THREE.Clock();
    this._started  = false;
    this._stopping = false;
  }

  init(maxTrack = 1, targetSrc = '') {
    this.mindar = new MindARThree({
      container:      this.container,
      imageTargetSrc: targetSrc,
      maxTrack,
      /* Tối ưu cho sản phẩm in thật — nhận diện nhanh hơn:
       * filterMinCF thấp → chấp nhận match yếu hơn
       * filterBeta vừa phải → mượt nhưng không lag
       * warmupTolerance thấp → confirm found nhanh hơn
       * missTolerance cao → không mất target khi bị che nhẹ */
      filterMinCF:     0.001,
      filterBeta:      1,
      warmupTolerance: 2,
      missTolerance:   10,
      uiLoading:  'no',
      uiError:    'no',
      uiScanning: 'no'
    });

    /* Patch resize bug */
    const originalResize = this.mindar.resize.bind(this.mindar);
    this.mindar.resize = function () {
      if (!this.controller) {
        return;
      }
      originalResize();
    };

    this.renderer = this.mindar.renderer;
    this.scene    = this.mindar.scene;
    this.camera   = this.mindar.camera;

    /* HUD renderer */
    this.hudRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.hudRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.hudRenderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.hudRenderer.setClearColor(0x000000, 0);
    this.hudRenderer.domElement.style.position = 'absolute';
    this.hudRenderer.domElement.style.inset = '0';
    this.hudRenderer.domElement.style.zIndex = '10';
    this.hudRenderer.domElement.style.pointerEvents = 'none';
    this.container.appendChild(this.hudRenderer.domElement);

    this.hudScene  = new THREE.Scene();
    this.hudCamera = new THREE.PerspectiveCamera(
      75,
      this.container.clientWidth / this.container.clientHeight,
      0.01, 
      100
    );
    this.hudCamera.position.set(0, 0, 0);
    this.hudCamera.lookAt(0, 0, -1);
    this.hudScene.add(this.hudCamera);

    this.hudScene.add(new THREE.AmbientLight(0xffffff, 1.0));
    const hudDir = new THREE.DirectionalLight(0xffffff, 1.2);
    hudDir.position.set(2, 4, 3);
    this.hudScene.add(hudDir);
    const hudFill = new THREE.DirectionalLight(0xffffff, 0.4);
    hudFill.position.set(-2, -1, 2);
    this.hudScene.add(hudFill);

    this._setupLighting();
  }

  _setupLighting() {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.8));
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(2, 4, 3);
    this.scene.add(dir);
    const fill = new THREE.DirectionalLight(0x00c8bd, 0.3);
    fill.position.set(-2, -1, 2);
    this.scene.add(fill);
  }

  addAnchor(index) {
    return this.mindar.addAnchor(index);
  }

  async start() {
    await this.mindar.start();
    this._started = true;

    this._ro = new ResizeObserver(() => {
      if (!this._started) return;
      const w = this.container.clientWidth;
      const h = this.container.clientHeight;
      if (this.hudCamera) {
        this.hudCamera.aspect = w / h;
        this.hudCamera.updateProjectionMatrix();
      }
      if (this.hudRenderer) this.hudRenderer.setSize(w, h);
    });
    this._ro.observe(this.container);
  }

  startRenderLoop(onFrame) {
    this.renderer.setAnimationLoop(() => {
      const dt = this._clock.getDelta();
      const t  = this._clock.getElapsedTime();
      onFrame({ dt, t });
      this.renderer.render(this.scene, this.camera);
      this.hudRenderer.render(this.hudScene, this.hudCamera);
    });
  }

  stopRenderLoop() {
    if (this.renderer) this.renderer.setAnimationLoop(null);
  }

  stop() {
    /* Guard: tránh stop 2 lần hoặc stop khi chưa init */
    if (this._stopping) return;
    this._stopping = true;
    this._started  = false;

    if (this._ro) {
      this._ro.disconnect();
      this._ro = null;
    }

    this.stopRenderLoop();

    if (this.hudRenderer) {
      this.hudRenderer.setAnimationLoop(null);
      this.hudRenderer.dispose();
      if (this.hudRenderer.domElement?.parentNode) {
        this.hudRenderer.domElement.remove();
      }
      this.hudRenderer = null;
    }

    /* MindAR stop — chỉ gọi nếu controller đã tồn tại */
    try {
      if (this.mindar?.controller) {
        this.mindar.stop();
      } else if (this.mindar) {
        /* controller chưa ready — dừng video thủ công */
        const video = this.container?.querySelector('video');
        if (video?.srcObject) {
          video.srcObject.getTracks().forEach(t => t.stop());
          video.srcObject = null;
        }
      }
    } catch (e) {
      console.warn('[AREngine] stop error (ignored):', e.message);
    }

    this.mindar    = null;
    this.renderer  = null;
    this._stopping = false;
  }

  getDelta() { 
    return this._clock.getDelta(); 
  }
}
